"use client";

import React, { useEffect, useMemo } from "react";
import { useCookie } from "react-use";
import clientFetcher, {
  COOKIE_GLOBAL_NAME,
  PredictCodResponse,
  Product,
  PromotionResult,
  PromotionResultType,
} from "src/lib/fetcher/client";
import { setClientAssetImageScope } from "src/lib/utils/asset-image-scope";

export interface ICartPrice {
  shipping: number;
  discount: number;
  tax: number;
  price: number;
  amount: number;
}

export interface IGlobalDefaultContext {
  tenantId: string;
  saleChannelId: string;
  cartItems: Array<{ product: Product; quantity: number }>;
  promotionResult: PromotionResult | undefined;
  predictCostOnDeliveryResult: PredictCodResponse | undefined;
  cleanCart: () => void;
  updateProductQuantity: (payload: { product: any; quantity: number }) => void;
  updateCookie: (values: { [key: string]: any }) => void;
  updatePromotionResult: (promotion: PromotionResult) => void;
  updatePredictCostOnDeliveryResult: (promotion: PredictCodResponse) => void;
  getPrice: () => ICartPrice;
}

export interface GlobalDefaultContextRegistryProps {
  cartItems?: IGlobalDefaultContext["cartItems"];
  promotionResult?: IGlobalDefaultContext["promotionResult"];
  predictCostOnDeliveryResult?: IGlobalDefaultContext["predictCostOnDeliveryResult"];
  orgId: string;
  tenantId: string;
  saleChannelId: string;
  children: React.ReactNode;
}

export const GlobalDefaultContext = React.createContext<IGlobalDefaultContext>({} as any);

export const useGlobalContext = () =>
  React.useContext<IGlobalDefaultContext>((globalThis as any).GlobalContext);

export default function GlobalDefaultContextRegistry(props: GlobalDefaultContextRegistryProps) {
  const [_cookieValue, _updateCookie] = useCookie(COOKIE_GLOBAL_NAME);
  const [cartItems, setCartItems] = React.useState(props.cartItems ?? []);
  const [promotionResult, setPromotionResult] = React.useState(props.promotionResult);
  const [predictCostOnDeliveryResult, setPredictCostOnDeliveryResult] = React.useState(
    props.predictCostOnDeliveryResult,
  );
  const [cookieValue, setCookie] = React.useState<any>(undefined);

  (globalThis as any).GlobalContext = GlobalDefaultContext;
  setClientAssetImageScope({ orgId: props.orgId, tenantId: props.tenantId });

  const updateCookie = (value: { [key: string]: any }) => {
    setCookie((previous: any) => ({ ...previous, ...value }));
  };

  useEffect(() => {
    props.tenantId && (clientFetcher.tenantId = props.tenantId);
    props.saleChannelId && (clientFetcher.saleChannelId = props.saleChannelId);
  }, [props.tenantId, props.saleChannelId]);

  useEffect(() => {
    let previous: any = _cookieValue && atob(_cookieValue);
    // prettier-ignore
    try { previous = JSON.parse(previous); } catch (e) {}
    const cookieValue = previous && typeof previous === "object" ? previous : {};

    cookieValue.cart && ((clientFetcher.client.defaults.env as any).cart = cookieValue.cart);
    (clientFetcher.client.defaults.env as any).cookieValue = cookieValue;
    (clientFetcher.client.defaults.env as any).updateCookie = updateCookie;

    setCookie(cookieValue);

    if (
      (cookieValue && props && cookieValue.orgId !== props.orgId) ||
      (cookieValue && props && cookieValue.tenantId !== props.tenantId) ||
      cookieValue.saleChannelId !== props.saleChannelId
    ) {
      updateCookie({
        ...(cookieValue.orgId !== props.orgId && { orgId: props.orgId }),
        ...(cookieValue.tenantId !== props.tenantId && { tenantId: props.tenantId }),
        ...(cookieValue.saleChannelId !== props.saleChannelId && {
          saleChannelId: props.saleChannelId,
        }),
      });
    }
  }, [_cookieValue]);

  useEffect(() => {
    if (cookieValue) {
      (clientFetcher as any)._global = cookieValue;
      _updateCookie(btoa(JSON.stringify(cookieValue)), {
        expires: 365,
        sameSite: "Lax",
      });
    }
  }, [cookieValue]);

  const value: IGlobalDefaultContext = useMemo(
    () => ({
      tenantId: props.tenantId,
      saleChannelId: props.saleChannelId,
      cartItems: cartItems,
      promotionResult: promotionResult,
      predictCostOnDeliveryResult: predictCostOnDeliveryResult,
      updateCookie,
      getPrice: () => {
        const totalPrice = cartItems.reduce(
          (acc, { product, quantity }) => acc + product.salePrice * quantity,
          0,
        );
        let shippingPrice = predictCostOnDeliveryResult?.estimatedCostOnDelivery ?? 0;
        let discount = 0;
        if (promotionResult) {
          if (promotionResult.resultType === PromotionResultType.DiscountAmount) {
            discount = promotionResult?.value;
          }
          if (promotionResult.resultType === PromotionResultType.DiscountPercentage) {
            discount = (totalPrice * promotionResult?.value) / 100;
          }
          if (promotionResult.resultType === PromotionResultType.FreeShipping) {
            shippingPrice = 0;
          }
        }
        const tax = (totalPrice + shippingPrice) * 0;
        const finalPrice = totalPrice + shippingPrice + tax - discount;
        const result: ICartPrice = {
          amount: finalPrice,
          discount,
          price: totalPrice,
          shipping: shippingPrice,
          tax,
        };
        return result;
      },
      cleanCart: () => {
        setCartItems([]);
        clientFetcher.cart = null;
        updateCookie({ cart: undefined });
        setPromotionResult(undefined);
      },
      updateProductQuantity: async (payload) => {
        setCartItems((list) => {
          const index = list.findIndex(({ product: { id } }) => id === payload.product.id);

          if (~index) {
            if (payload.quantity <= 0) list.splice(index, 1);
            else list[index] = payload;
          } else if (payload.quantity > 0) list.push(payload);

          return [...list];
        });

        await clientFetcher.addToCart({
          productId: payload.product.id,
          quantity: payload.quantity,
        });
      },
      updatePredictCostOnDeliveryResult: async (result: PredictCodResponse) => {
        setPredictCostOnDeliveryResult(result);
      },
      updatePromotionResult: async (result: PromotionResult) => {
        setPromotionResult(result);
        await clientFetcher.addPromotionToCart(result);
      },
    }),
    [props.tenantId, props.saleChannelId, cartItems, promotionResult, predictCostOnDeliveryResult],
  );

  return (
    <GlobalDefaultContext.Provider value={value}>{props.children}</GlobalDefaultContext.Provider>
  );
}
